import React, { useEffect, useState, useCallback } from 'react';
import {
  Settings, CreditCard, Mail, BarChart, Link, Image,
  Shield, Globe, Zap, Lock, Terminal,
  CheckCircle, AlertCircle, Loader, RefreshCw, Smartphone,
  Info, Bell, Clock, Database, Server, Key, Eye, EyeOff,
  ChevronRight, Save, Layout, Cloud,
  Tag, Percent, User, Layers, Code, Activity, FileText, Crosshair,
  ShieldCheck
} from 'lucide-react';
import api from '../../services/api';
import PageHeader from '../../components/PageHeader';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';

const SECTIONS = [
  { key: 'general',  label: 'Core Configuration',    icon: Globe, color: 'text-[#0176D3]' },
  { key: 'payment',  label: 'Financial Interfaces',   icon: CreditCard, color: 'text-[#2E844A]' },
  { key: 'business', label: 'GST & Billing Identity', icon: FileText, color: 'text-[#2E844A]' },
  { key: 'smtp',     label: 'Email Gateway',       icon: Mail, color: 'text-[#0176D3]' },
  { key: 'sms',      label: 'SMS Gateway',         icon: Smartphone, color: 'text-[#0176D3]' },
  { key: 'limits',   label: 'Limits', icon: BarChart, color: 'text-[#DD7A01]' },
  { key: 'api',      label: 'Integrations', icon: Terminal, color: 'text-[#0176D3]' },
  { key: 'imagekit', label: 'Cloud Infrastructure',  icon: Cloud, color: 'text-[#BA0517]' },
  { key: 'storage',  label: 'Cloud Storage Connectors', icon: Link, color: 'text-[#0176D3]' },
];

const STORAGE_PROVIDER_META = {
  google_drive: { label: 'Google Drive', hint: 'Google Cloud Console → OAuth 2.0 Client (Web application)' },
  dropbox: { label: 'Dropbox', hint: 'Dropbox App Console → App key / App secret' },
  box: { label: 'Box', hint: 'Box Developer Console → OAuth 2.0 Credentials' },
  onedrive: { label: 'OneDrive', hint: 'Azure AD App Registration → Application (client) ID / Client secret' },
};
const SECRET_MASK_PLACEHOLDER = '••••••••••••••••';

function Toggle({ label, desc, checked, onChange }) {
  return (
    <label className="flex items-center justify-between p-4 rounded-md bg-[#F4F6F9] border border-[#D8DDE6] hover:bg-[#ECEBEA] transition-colors cursor-pointer outline-none">
      <div className="space-y-0.5">
        <div className="text-[13px] font-semibold text-[#080707]">{label}</div>
        {desc && <div className="text-[12px] text-[#706E6B]">{desc}</div>}
      </div>
      <div className={cn(
 "w-11 h-6 rounded-full relative transition-colors duration-200 ease-in-out",
 checked ? "bg-[#0176D3]" : "bg-[#B0ADAB]"
 )}>
        <div className={cn(
 "absolute top-1 w-4 h-4 rounded-full bg-white transition-transform duration-200 ease-in-out",
 checked ? "translate-x-6" : "translate-x-1"
 )} />
      </div>
      <input type="checkbox" className="hidden" checked={!!checked} onChange={e => onChange(e.target.checked)} />
    </label>
  );
}

export default function SuperAdminSettings() {
  const [activeSection, setActiveSection] = useState('general');
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [errors, setErrors] = useState({});
  const [storageApps, setStorageApps] = useState({});
  const [storageLoading, setStorageLoading] = useState(true);
  const [storageSaving, setStorageSaving] = useState(false);

  const handleTestSmtp = async () => {
    if (!settings.smtpHost || !settings.smtpUser || !settings.smtpPass) {
      alertService.error('Missing Parameters', 'Enter SMTP host, user, and password before testing.');
      return;
    }

    try {
      setTestingSmtp(true);
      const res = await api.post('/admin/settings/test-smtp', {
        smtpHost: settings.smtpHost,
        smtpPort: settings.smtpPort,
        smtpUser: settings.smtpUser,
        smtpPass: settings.smtpPass,
        smtpSecure: settings.smtpSecure,
      });
      if (res.data.success) {
        alertService.toast('success', `Handshake verified: ${res.data.message}`);
      }
    } catch (e) {
      alertService.error('Connection failed', e.response?.data?.message || e.response?.data?.error || 'Server refused the connection. Check the SMTP settings.');
    } finally {
      setTestingSmtp(false);
    }
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/settings');
      const flat = {};
      Object.values(res.data.settings || {}).forEach(catSettings => {
        Object.assign(flat, catSettings);
      });
      setSettings(flat);
    } catch (e) {
      alertService.error('Network Failure', e.response?.data?.error || 'Failed to load system configuration.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Kept separate from `settings`/`load()`/`handleSave` above: these credentials are
  // masked server-side (never echoed back) and encrypted at rest via a dedicated
  // endpoint (/admin/storage-oauth-apps), unlike the plaintext-round-tripped blob the
  // rest of this page saves through /admin/settings.
  const loadStorageApps = useCallback(async () => {
    try {
      setStorageLoading(true);
      const res = await api.get('/admin/storage-oauth-apps');
      setStorageApps(res.data.data || {});
    } catch (e) {
      alertService.error('Network Failure', e.response?.data?.error || 'Failed to load storage connector settings.');
    } finally {
      setStorageLoading(false);
    }
  }, []);

  useEffect(() => { load(); loadStorageApps(); }, [load, loadStorageApps]);

  const setStorageField = (provider, field, value) => {
    setStorageApps(s => ({ ...s, [provider]: { ...(s[provider] || {}), [field]: value } }));
  };

  const handleSaveStorageApps = async () => {
    try {
      setStorageSaving(true);
      const body = {};
      for (const provider of Object.keys(STORAGE_PROVIDER_META)) {
        const app = storageApps[provider] || {};
        body[provider] = {
          clientId: app.clientId || '',
          // Only send clientSecret when the operator actually typed a new value —
          // an empty/untouched field must never clobber what's already saved.
          ...(app.clientSecret ? { clientSecret: app.clientSecret } : {}),
          ...(provider === 'onedrive' ? { tenantId: app.tenantId || '' } : {}),
        };
      }
      await api.put('/admin/storage-oauth-apps', body);
      alertService.toast('success', 'Storage connector credentials saved.');
      await loadStorageApps(); // refresh clientSecretSet flags, clear the typed secret from local state
    } catch (e) {
      alertService.error('Save Failed', e.response?.data?.error || 'Failed to save storage connector credentials.');
    } finally {
      setStorageSaving(false);
    }
  };

  const set = (key, value) => {
    setSettings(s => ({ ...s, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: null }));
  };

  const validate = () => {
    const newErrors = {};
    if (!settings.platformName) newErrors.platformName = 'Platform name is required';
    if (!settings.platformUrl) newErrors.platformUrl = 'Platform URL is required';
    if (!settings.supportEmail) newErrors.supportEmail = 'Support email is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      alertService.toast('error', 'Please resolve configuration validation errors');
      return;
    }

    try {
      setSaving(true);
      const categoryMap = {
        general: ['platformName', 'platformUrl', 'supportEmail', 'maintenanceMode', 'registrationEnabled'],
        // Read by src/utils/platformGstSplit.js to decide CGST+SGST
        // (same-state) vs IGST (different-state) on every subscription
        // invoice this platform issues to a tenant — without a gstin+state
        // on file here, every invoice stays 'unregistered' (no GST charged).
        business: ['gstin', 'name', 'state', 'address'],
        payment: [
          'razorpayKeyId', 'razorpayKeySecret', 'razorpayWebhookSecret',
          'stripeSecretKey', 'stripeWebhookSecret',
          'cashfreeClientId', 'cashfreeClientSecret', 'cashfreeWebhookSecret',
          'payuMerchantKey', 'payuMerchantSalt',
          'ccavenueMerchantId', 'ccavenueAccessCode', 'ccavenueWorkingKey',
          'paymentMode', 'currency', 'taxRate', 'bankTransferEnabled', 'bankTransferDetails',
        ],
        smtp: ['smtpHost', 'smtpPort', 'smtpUser', 'smtpPass', 'smtpFrom', 'smtpSecure'],
        sms: ['smsProvider', 'smsApiKey', 'smsAccountSid', 'smsFromNumber', 'smsSenderId', 'smsRoute'],
        limits: ['defaultMaxDevices', 'defaultMaxMessages', 'globalMessageDelay', 'messagesPerMinute', 'maxBulkContacts', 'sessionTimeout'],
        api:      ['apiRateLimit', 'apiWhitelist', 'webhookTimeout', 'jwtExpiry', 'googlePlacesApiKey'],
        imagekit: ['imagekitUrlEndpoint', 'imagekitPublicKey', 'imagekitPrivateKey'],
      };

      const toSave = {};
      Object.entries(categoryMap).forEach(([cat, keys]) => {
        keys.forEach(k => {
          if (settings[k] !== undefined) {
            if (!toSave[cat]) toSave[cat] = {};
            toSave[cat][k] = settings[k];
          }
        });
      });

      await api.put('/admin/settings', { settings: toSave });
      alertService.toast('success', 'System parameters preserved successfully');
    } catch (e) {
      alertService.error('Preservation Failed', e.response?.data?.error || 'Failed to preserve system configuration.');
    } finally {
      setSaving(false);
    }
  };

  const currentSection = SECTIONS.find(s => s.key === activeSection);

  const renderSection = () => {
    switch (activeSection) {
      case 'general':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
               <div className="space-y-2">
                  <label className="text-[12px] font-semibold text-[#444444]">Application Title</label>
                  <Input 
                    icon={Tag}
                    value={settings.platformName || ''} 
                    onChange={e => set('platformName', e.target.value)} 
                    placeholder="e.g. WaapLeads"
                    error={errors.platformName}
                    hint="Brand designation across platform"
                  />
               </div>
               <div className="space-y-2">
                  <label className="text-[12px] font-semibold text-[#444444]">Public Endpoint URL</label>
                  <Input 
                    icon={Globe}
                    value={settings.platformUrl || ''} 
                    onChange={e => set('platformUrl', e.target.value)} 
                    placeholder="https://app.waapleads.com"
                    error={errors.platformUrl}
                    hint="Primary production domain"
                  />
               </div>
            </div>
            <div className="space-y-2">
               <label className="text-[12px] font-semibold text-[#444444]">Administrative Support Email</label>
               <Input 
                 icon={Mail}
                 type="email" 
                 value={settings.supportEmail || ''} 
                 onChange={e => set('supportEmail', e.target.value)} 
                 placeholder="support@waapleads.com"
                 error={errors.supportEmail}
                 hint="System contact for user support"
               />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6">
              <Toggle
                label="Maintenance Mode"
                desc="Restrict access to system administrators"
                checked={settings.maintenanceMode}
                onChange={v => set('maintenanceMode', v)}
              />
              <Toggle
                label="User Registration"
                desc="Authorize new account creation"
                checked={settings.registrationEnabled !== false}
                onChange={v => set('registrationEnabled', v)}
              />
            </div>
          </div>
        );

      case 'business':
        return (
          <div className="space-y-6">
            <p className="text-[12px] text-[#706E6B]">
              This platform's own registered GST details — printed as the "seller" on every subscription invoice issued
              to a tenant, and used to decide CGST+SGST (same state as the tenant) vs IGST (different state). Leave GSTIN
              blank if this platform doesn't charge GST on subscriptions; invoices will show as tax-unregistered.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[12px] font-semibold text-[#444444]">Registered Business Name</label>
                <Input
                  icon={Tag}
                  value={settings.name || ''}
                  onChange={e => set('name', e.target.value)}
                  placeholder="e.g. WaapCoders Pvt Ltd"
                  hint="Printed as the seller on invoices"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[12px] font-semibold text-[#444444]">GSTIN</label>
                <Input
                  icon={FileText}
                  value={settings.gstin || ''}
                  onChange={e => set('gstin', e.target.value.toUpperCase())}
                  placeholder="e.g. 27ABCDE1234F1Z5"
                  hint="Blank = no GST charged on subscriptions"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[12px] font-semibold text-[#444444]">Registered State</label>
                <Input
                  icon={Globe}
                  value={settings.state || ''}
                  onChange={e => set('state', e.target.value)}
                  placeholder="e.g. Maharashtra"
                  hint="Compared against each tenant's state for CGST+SGST vs IGST"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[12px] font-semibold text-[#444444]">Registered Address</label>
                <Input
                  icon={Layers}
                  value={settings.address || ''}
                  onChange={e => set('address', e.target.value)}
                  placeholder="Full registered business address"
                />
              </div>
            </div>
          </div>
        );

      case 'payment':
        return (
          <div className="space-y-8">
            <Card className="flex items-center gap-4 bg-[#F4F6F9] border border-[#0176D3] p-4 rounded-lg">
                <div className="w-10 h-10 rounded-md bg-[#E0F2FE] flex items-center justify-center text-[#0176D3]">
                  <ShieldCheck size={20} />
                </div>
                <p className="text-[13px] font-semibold text-[#080707]">Financial interfaces utilize RSA-4096 encryption. Modified parameters will be integrated upon preservation.</p>
            </Card>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
               <div className="space-y-2">
                  <label className="text-[12px] font-semibold text-[#444444]">System Currency</label>
                  <Input 
                    icon={Database}
                    value={settings.currency || ''} 
                    onChange={e => set('currency', e.target.value)} 
                    placeholder="INR"
                    hint="ISO 4217 standard code"
                  />
               </div>
               <div className="space-y-2">
                  <label className="text-[12px] font-semibold text-[#444444]">Applied Tax Rate (%)</label>
                  <Input 
                    icon={Percent}
                    type="number" 
                    min="0" 
                    max="100" 
                    value={settings.taxRate || ''} 
                    onChange={e => set('taxRate', e.target.value)} 
                    placeholder="18"
                    hint="Global taxation percentage"
                  />
               </div>
               <div className="space-y-2">
                  <label className="text-[12px] font-semibold text-[#444444]">Gateway Operations</label>
                  <Select 
                    icon={Zap}
                    value={settings.paymentMode || 'test'} 
                    onChange={e => set('paymentMode', e.target.value)}
                    hint="Switch between Sandbox and Live"
                  >
                    <option value="test">Sandbox / Testing</option>
                    <option value="live">Live / Production</option>
                  </Select>
               </div>
            </div>

            <div className="pt-6 border-t border-[#D8DDE6] space-y-4">
              <div className="flex items-center gap-2">
                 <div className="w-8 h-8 rounded-md bg-[#E0F2FE] flex items-center justify-center text-[#0176D3]">
                    <Activity size={16} />
                 </div>
                 <h3 className="text-[14px] font-bold text-[#080707]">Razorpay Interface</h3>
              </div>
              <div className="p-4 rounded-md bg-[#EAF4FE] border border-[#0176D3]/20 text-[13px] text-[#0176D3] font-medium">
                Razorpay keys now live in the encrypted Platform Integrations vault, not here — go to{' '}
                <strong>Super Admin → Platform Integrations</strong> to set or rotate the platform's Razorpay Key ID and Key Secret.
                This keeps them encrypted at rest instead of stored as plain text, and lets them be rotated without a redeploy.
              </div>
            </div>

            <div className="pt-6 border-t border-[#D8DDE6] space-y-6">
              <div className="flex items-center gap-2">
                 <div className="w-8 h-8 rounded-md bg-[#E0F2FE] flex items-center justify-center text-[#0176D3]">
                    <Activity size={16} />
                 </div>
                 <h3 className="text-[14px] font-bold text-[#080707]">Stripe Interface (International)</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 <div className="space-y-2">
                    <label className="text-[12px] font-semibold text-[#444444]">Secret Key</label>
                    <Input
                       icon={Lock}
                       type={showSecrets ? 'text' : 'password'}
                       value={settings.stripeSecretKey || ''}
                       onChange={e => set('stripeSecretKey', e.target.value)}
                       placeholder="sk_live_…"
                    />
                 </div>
                 <div className="space-y-2">
                    <label className="text-[12px] font-semibold text-[#444444]">Webhook Signing Secret</label>
                    <Input
                       icon={Terminal}
                       type={showSecrets ? 'text' : 'password'}
                       value={settings.stripeWebhookSecret || ''}
                       onChange={e => set('stripeWebhookSecret', e.target.value)}
                       placeholder="whsec_…"
                    />
                 </div>
              </div>
            </div>

            <div className="pt-6 border-t border-[#D8DDE6] space-y-6">
              <div className="flex items-center gap-2">
                 <div className="w-8 h-8 rounded-md bg-[#E0F2FE] flex items-center justify-center text-[#0176D3]">
                    <Activity size={16} />
                 </div>
                 <h3 className="text-[14px] font-bold text-[#080707]">Cashfree Interface</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                 <div className="space-y-2">
                    <label className="text-[12px] font-semibold text-[#444444]">Client ID</label>
                    <Input icon={Key} value={settings.cashfreeClientId || ''} onChange={e => set('cashfreeClientId', e.target.value)} />
                 </div>
                 <div className="space-y-2">
                    <label className="text-[12px] font-semibold text-[#444444]">Client Secret</label>
                    <Input icon={Lock} type={showSecrets ? 'text' : 'password'} value={settings.cashfreeClientSecret || ''} onChange={e => set('cashfreeClientSecret', e.target.value)} />
                 </div>
                 <div className="space-y-2">
                    <label className="text-[12px] font-semibold text-[#444444]">Webhook Secret</label>
                    <Input icon={Terminal} type={showSecrets ? 'text' : 'password'} value={settings.cashfreeWebhookSecret || ''} onChange={e => set('cashfreeWebhookSecret', e.target.value)} />
                 </div>
              </div>
            </div>

            <div className="pt-6 border-t border-[#D8DDE6] space-y-6">
              <div className="flex items-center gap-2">
                 <div className="w-8 h-8 rounded-md bg-[#E0F2FE] flex items-center justify-center text-[#0176D3]">
                    <Activity size={16} />
                 </div>
                 <h3 className="text-[14px] font-bold text-[#080707]">PayU Interface</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 <div className="space-y-2">
                    <label className="text-[12px] font-semibold text-[#444444]">Merchant Key</label>
                    <Input icon={Key} value={settings.payuMerchantKey || ''} onChange={e => set('payuMerchantKey', e.target.value)} />
                 </div>
                 <div className="space-y-2">
                    <label className="text-[12px] font-semibold text-[#444444]">Merchant Salt</label>
                    <Input icon={Lock} type={showSecrets ? 'text' : 'password'} value={settings.payuMerchantSalt || ''} onChange={e => set('payuMerchantSalt', e.target.value)} />
                 </div>
              </div>
            </div>

            <div className="pt-6 border-t border-[#D8DDE6] space-y-6">
              <div className="flex items-center gap-2">
                 <div className="w-8 h-8 rounded-md bg-[#E0F2FE] flex items-center justify-center text-[#0176D3]">
                    <Activity size={16} />
                 </div>
                 <h3 className="text-[14px] font-bold text-[#080707]">CCAvenue Interface</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                 <div className="space-y-2">
                    <label className="text-[12px] font-semibold text-[#444444]">Merchant ID</label>
                    <Input icon={Key} value={settings.ccavenueMerchantId || ''} onChange={e => set('ccavenueMerchantId', e.target.value)} />
                 </div>
                 <div className="space-y-2">
                    <label className="text-[12px] font-semibold text-[#444444]">Access Code</label>
                    <Input icon={Key} value={settings.ccavenueAccessCode || ''} onChange={e => set('ccavenueAccessCode', e.target.value)} />
                 </div>
                 <div className="space-y-2">
                    <label className="text-[12px] font-semibold text-[#444444]">Working Key</label>
                    <Input icon={Lock} type={showSecrets ? 'text' : 'password'} value={settings.ccavenueWorkingKey || ''} onChange={e => set('ccavenueWorkingKey', e.target.value)} />
                 </div>
              </div>
            </div>

            <div className="pt-6 border-t border-[#D8DDE6] space-y-6">
              <Toggle
                label="Manual Asset Transfer"
                desc="Authorize billing via direct bank transfer"
                checked={settings.bankTransferEnabled}
                onChange={v => set('bankTransferEnabled', v)}
              />
              {settings.bankTransferEnabled && (
                <div className="space-y-2">
                  <label className="text-[12px] font-semibold text-[#444444]">Settlement Details</label>
                  <textarea
                    className="w-full bg-white border border-[#D8DDE6] rounded p-4 text-[13px] text-[#080707] focus:border-[#0176D3] min-h-[120px] transition-colors focus:ring-1 focus:ring-[#0176D3] outline-none"
                    value={settings.bankTransferDetails || ''}
                    onChange={e => set('bankTransferDetails', e.target.value)}
                    placeholder={"Bank Identity: ...\nAccount Identifier: ...\nRouting Signature: ..."}
                  />
                </div>
              )}
            </div>
          </div>
        );

      case 'smtp':
        return (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
               <div className="md:col-span-3 space-y-2">
                  <label className="text-[12px] font-semibold text-[#444444]">Mail Server Host</label>
                  <Input 
                    icon={Server}
                    value={settings.smtpHost || ''} 
                    onChange={e => set('smtpHost', e.target.value)} 
                    placeholder="smtp.provider.net"
                  />
               </div>
               <div className="space-y-2">
                  <label className="text-[12px] font-semibold text-[#444444]">Port</label>
                  <Input 
                    icon={Database}
                    type="number" 
                    value={settings.smtpPort || ''} 
                    onChange={e => set('smtpPort', e.target.value)} 
                    placeholder="587"
                  />
               </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                 <label className="text-[12px] font-semibold text-[#444444]">Auth Username</label>
                 <Input 
                   icon={User}
                   value={settings.smtpUser || ''} 
                   onChange={e => set('smtpUser', e.target.value)} 
                   placeholder="identity@domain.com"
                 />
              </div>
              <div className="space-y-2">
                 <label className="text-[12px] font-semibold text-[#444444]">Auth Password</label>
                 <Input 
                   icon={Lock}
                   type={showSecrets ? 'text' : 'password'} 
                   value={settings.smtpPass || ''} 
                   onChange={e => set('smtpPass', e.target.value)} 
                   placeholder="••••••••••••••••"
                 />
              </div>
            </div>
            <div className="space-y-2">
               <label className="text-[12px] font-semibold text-[#444444]">Primary Sender Identity</label>
               <Input 
                 icon={Layout}
                 value={settings.smtpFrom || ''} 
                 onChange={e => set('smtpFrom', e.target.value)} 
                 placeholder='"System Gateway" <noreply@domain.com>'
                 hint="Designated email signature for outbound traffic"
               />
            </div>
            <div className="pt-6 flex flex-wrap items-center gap-4">
               <div className="flex-1 min-w-[200px]">
                 <Toggle
                   label="SSL/TLS Encryption"
                   desc="Recommended for secure message transmission"
                   checked={settings.smtpSecure}
                   onChange={v => set('smtpSecure', v)}
                 />
               </div>
               <button 
                className="h-9 px-4 bg-white border border-[#C9C9C9] text-[#080707] text-[13px] font-semibold rounded whitespace-nowrap hover:bg-[#F3F2F2] transition-colors flex items-center gap-2"
                onClick={() => setShowSecrets(!showSecrets)} 
              >
                  {showSecrets ? <EyeOff size={16} /> : <Eye size={16} />}
                  {showSecrets ? 'Mask Server Key' : 'Reveal Server Key'}
               </button>
               <button 
                className="h-9 px-4 bg-white border border-[#0176D3] text-[#0176D3] text-[13px] font-semibold rounded whitespace-nowrap hover:bg-[#F4F6F9] transition-colors flex items-center gap-2"
                onClick={handleTestSmtp} 
                disabled={testingSmtp}
              >
                  {testingSmtp ? <Loader size={16} className="animate-spin" /> : <Zap size={16} />}
                  Test Handshake
               </button>
            </div>
          </div>
        );

      case 'limits':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
               <div className="space-y-2">
                  <label className="text-[12px] font-semibold text-[#444444]">Standard Device Allotment</label>
                  <Input 
                    icon={Smartphone}
                    type="number" 
                    min="1" 
                    value={settings.defaultMaxDevices || ''} 
                    onChange={e => set('defaultMaxDevices', e.target.value)} 
                    hint="Constraint for tier-zero accounts"
                  />
               </div>
               <div className="space-y-2">
                  <label className="text-[12px] font-semibold text-[#444444]">Standard Message Limit</label>
                  <Input 
                    icon={BarChart}
                    type="number" 
                    min="1" 
                    value={settings.defaultMaxMessages || ''} 
                    onChange={e => set('defaultMaxMessages', e.target.value)} 
                    hint="Monthly volume limit for default plans"
                  />
               </div>
               <div className="space-y-2">
                  <label className="text-[12px] font-semibold text-[#444444]">Sync Delay (ms)</label>
                  <Input 
                    icon={Clock}
                    type="number" 
                    min="500" 
                    value={settings.globalMessageDelay || ''} 
                    onChange={e => set('globalMessageDelay', e.target.value)} 
                    hint="Latency between sequential signals"
                  />
               </div>
               <div className="space-y-2">
                  <label className="text-[12px] font-semibold text-[#444444]">Traffic Velocity</label>
                  <Input 
                    icon={Zap}
                    type="number" 
                    min="1" 
                    value={settings.messagesPerMinute || ''} 
                    onChange={e => set('messagesPerMinute', e.target.value)} 
                    hint="Messages per minute for account stability"
                  />
               </div>
               <div className="space-y-2">
                  <label className="text-[12px] font-semibold text-[#444444]">Bulk Operation Volume</label>
                  <Input 
                    icon={Layers}
                    type="number" 
                    min="1" 
                    value={settings.maxBulkContacts || ''} 
                    onChange={e => set('maxBulkContacts', e.target.value)} 
                    hint="Max identifiers per broadcast sequence"
                  />
               </div>
               <div className="space-y-2">
                  <label className="text-[12px] font-semibold text-[#444444]">Auth Session Lifespan (hr)</label>
                  <Input 
                    icon={Lock}
                    type="number" 
                    min="1" 
                    value={settings.sessionTimeout || ''} 
                    onChange={e => set('sessionTimeout', e.target.value)} 
                    hint="Automatic logout threshold"
                  />
               </div>
            </div>
          </div>
        );

      case 'api':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
               <div className="space-y-2">
                  <label className="text-[12px] font-semibold text-[#444444]">Auth Gate Rate Limit</label>
                  <Input 
                    icon={Zap}
                    type="number" 
                    min="1" 
                    value={settings.apiRateLimit || ''} 
                    onChange={e => set('apiRateLimit', e.target.value)} 
                    hint="Requests/min per security signature"
                  />
               </div>
               <div className="space-y-2">
                  <label className="text-[12px] font-semibold text-[#444444]">Webhook Latency Threshold (ms)</label>
                  <Input 
                    icon={Clock}
                    type="number" 
                    min="1000" 
                    value={settings.webhookTimeout || ''} 
                    onChange={e => set('webhookTimeout', e.target.value)} 
                    hint="Response window for external protocols"
                  />
               </div>
            </div>
            <div className="space-y-2">
               <label className="text-[12px] font-semibold text-[#444444]">JWT Signature Expiry</label>
               <Input 
                 icon={Key}
                 value={settings.jwtExpiry || ''} 
                 onChange={e => set('jwtExpiry', e.target.value)} 
                 placeholder="7d"
                 hint="Token lifetime (e.g. 7d, 24h)"
               />
            </div>
            <div className="space-y-2">
              <label className="text-[12px] font-semibold text-[#444444]">Integration IP Whitelist</label>
              <textarea
                className="w-full bg-white border border-[#D8DDE6] rounded p-4 text-[13px] text-[#080707] focus:border-[#0176D3] min-h-[100px] transition-colors focus:ring-1 focus:ring-[#0176D3] outline-none"
                rows={3}
                value={settings.apiWhitelist || ''}
                onChange={e => set('apiWhitelist', e.target.value)}
                placeholder="e.g. 192.168.1.1, 203.0.113.0/24"
              />
              <p className="text-[12px] text-[#706E6B]">Empty value allows traffic from all IPs.</p>
            </div>
            <div className="space-y-2">
               <label className="text-[12px] font-semibold text-[#444444]">Google Places API Key</label>
               <Input
                 icon={Key}
                 type={showSecrets ? 'text' : 'password'}
                 value={settings.googlePlacesApiKey || ''}
                 onChange={e => set('googlePlacesApiKey', e.target.value)}
                 placeholder="AIza…"
                 hint="Powers the Lead Scraper (Places text search + details). Without this, Lead Scraper stays disabled for every tenant."
               />
            </div>

            <Card className="flex flex-col gap-4 bg-[#F4F6F9] border-[#E5E5E5] p-6 rounded-lg">
               <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md bg-[#E0F2FE] flex items-center justify-center text-[#0176D3]">
                    <Code size={20} />
                  </div>
                  <div className="space-y-0.5">
                     <h3 className="text-[14px] font-bold text-[#080707] leading-none">Public Gateway Endpoint</h3>
                     <p className="text-[12px] text-[#706E6B]">Base URL for all integration requests</p>
                  </div>
               </div>
               <div className="p-4 rounded-lg bg-white border border-[#E5E5E5] text-[13px] text-[#080707] break-all select-all shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                 {settings.platformUrl || 'https://app.waapleads.com'}/api/client
               </div>
            </Card>
          </div>
        );

      case 'sms':
        return (
          <div className="space-y-6">
            <Card className="flex items-center gap-4 bg-[#F5F3FF] border border-[#A78BFA] p-4 rounded-lg">
              <div className="w-10 h-10 rounded-md bg-[#EDE9FE] flex items-center justify-center text-[#0176D3]">
                <Smartphone size={20} />
              </div>
              <div className="space-y-0.5">
                <h3 className="text-[14px] font-bold text-[#014486]">Platform-Level SMS Fallback</h3>
                <p className="text-[12px] text-[#0176D3]">
                  These credentials are used only when a tenant has not configured their own SMS gateway. Tenant keys always take priority.
                </p>
              </div>
            </Card>

            <div className="space-y-2">
              <label className="text-[12px] font-semibold text-[#444444]">Default SMS Provider</label>
              <select
                value={settings.smsProvider || 'msg91'}
                onChange={e => set('smsProvider', e.target.value)}
                className="w-full bg-white border border-[#E5E5E5] rounded px-4 h-9 text-[13px] text-[#080707] focus:border-[#0176D3] transition-colors"
              >
                <option value="msg91">MSG91 (India)</option>
                <option value="textlocal">TextLocal (India)</option>
                <option value="fast2sms">Fast2SMS (India)</option>
                <option value="2factor">2Factor (India)</option>
                <option value="twilio">Twilio (International)</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-[12px] font-semibold text-[#444444]">
                {settings.smsProvider === 'twilio' ? 'Auth Token' : 'API Key'}
              </label>
              <Input
                icon={Key}
                type={showSecrets ? 'text' : 'password'}
                value={settings.smsApiKey || ''}
                onChange={e => set('smsApiKey', e.target.value)}
                placeholder="••••••••••••••••"
              />
            </div>

            {settings.smsProvider === 'twilio' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[12px] font-semibold text-[#444444]">Account SID</label>
                  <Input
                    icon={Key}
                    value={settings.smsAccountSid || ''}
                    onChange={e => set('smsAccountSid', e.target.value)}
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[12px] font-semibold text-[#444444]">From Number</label>
                  <Input
                    icon={Smartphone}
                    value={settings.smsFromNumber || ''}
                    onChange={e => set('smsFromNumber', e.target.value)}
                    placeholder="+1415xxxxxxx"
                  />
                </div>
              </div>
            )}

            {settings.smsProvider !== 'twilio' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[12px] font-semibold text-[#444444]">Sender ID / DLT Header</label>
                  <Input
                    icon={Smartphone}
                    value={settings.smsSenderId || ''}
                    onChange={e => set('smsSenderId', e.target.value)}
                    placeholder="ADPLT"
                    hint="6-char DLT registered sender"
                  />
                </div>
                {settings.smsProvider === 'msg91' && (
                  <div className="space-y-2">
                    <label className="text-[12px] font-semibold text-[#444444]">Route</label>
                    <select
                      value={settings.smsRoute || '4'}
                      onChange={e => set('smsRoute', e.target.value)}
                      className="w-full bg-white border border-[#E5E5E5] rounded px-4 h-9 text-[13px] text-[#080707] focus:border-[#0176D3] transition-colors"
                    >
                      <option value="4">Route 4 — Transactional</option>
                      <option value="1">Route 1 — Promotional</option>
                    </select>
                  </div>
                )}
              </div>
            )}

            <button
              className="text-[#0176D3] text-[13px] font-semibold flex items-center gap-1 hover:underline"
              onClick={() => setShowSecrets(!showSecrets)}
            >
              {showSecrets ? <><EyeOff size={14} /> Mask Keys</> : <><Eye size={14} /> Reveal Keys</>}
            </button>
          </div>
        );

      case 'imagekit':
        return (
          <div className="space-y-6">
            <Card className="flex items-center gap-4 bg-[#FFF5F5] border border-[#FC8181] p-4 rounded-lg">
                <div className="w-10 h-10 rounded-md bg-[#FED7D7] flex items-center justify-center text-[#E53E3E]">
                  <Cloud size={20} />
                </div>
                <div className="space-y-0.5">
                   <h3 className="text-[14px] font-bold text-[#C53030]">Cloud Infrastructure Identity</h3>
                   <p className="text-[12px] text-[#C53030]">
                      Store platform media on a CDN. Required for message attachments.
                   </p>
                </div>
            </Card>

            <div className="space-y-2">
               <label className="text-[12px] font-semibold text-[#444444]">CDN Endpoint Signature</label>
               <Input 
                 icon={Globe}
                 value={settings.imagekitUrlEndpoint || ''} 
                 onChange={e => set('imagekitUrlEndpoint', e.target.value)} 
                 placeholder="https://ik.imagekit.io/unique_id"
                 hint="Individual CDN environment address"
               />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                 <label className="text-[12px] font-semibold text-[#444444]">Public Key Identifier</label>
                 <Input 
                   icon={Key}
                   value={settings.imagekitPublicKey || ''} 
                   onChange={e => set('imagekitPublicKey', e.target.value)} 
                   placeholder="public_…"
                 />
              </div>
              <div className="space-y-2">
                 <label className="text-[12px] font-semibold text-[#444444]">Private Security Key</label>
                 <Input 
                   icon={Shield}
                   type={showSecrets ? 'text' : 'password'} 
                   value={settings.imagekitPrivateKey || ''} 
                   onChange={e => set('imagekitPrivateKey', e.target.value)} 
                   placeholder="••••••••••••••••"
                 />
              </div>
            </div>
            <button
              className="text-[#0176D3] text-[13px] font-semibold flex items-center gap-1 hover:underline"
              onClick={() => setShowSecrets(!showSecrets)}
            >
              {showSecrets ? <><EyeOff size={14} /> Hide Keys</> : <><Eye size={14} /> Show Keys</>}
            </button>
          </div>
        );

      case 'storage':
        return (
          <div className="space-y-6">
            <Card className="flex items-center gap-4 bg-[#E0F2FE] border border-[#BAE6FD] p-4 rounded-lg">
              <div className="w-10 h-10 rounded-md bg-[#BAE6FD] flex items-center justify-center text-[#0176D3]">
                <Link size={20} />
              </div>
              <div className="space-y-0.5">
                <h3 className="text-[14px] font-bold text-[#0176D3]">Cloud Storage OAuth Apps</h3>
                <p className="text-[12px] text-[#0176D3]">
                  One OAuth app per provider, shared across every tenant on this platform. Each tenant's own "Connect"
                  click authorizes their own account through it — these credentials never grant access to tenant data
                  by themselves.
                </p>
              </div>
            </Card>

            {storageLoading ? (
              <div className="w-full py-16 flex items-center justify-center opacity-50">
                <Loader size={28} className="animate-spin text-[#0176D3]" />
              </div>
            ) : (
              Object.entries(STORAGE_PROVIDER_META).map(([provider, meta]) => {
                const app = storageApps[provider] || {};
                return (
                  <div key={provider} className="p-4 rounded-md border border-[#D8DDE6] bg-[#FAFBFC] space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-[13px] font-bold text-[#080707]">{meta.label}</h4>
                      <Badge className={cn(
 "text-[11px] font-semibold",
 app.clientSecretSet && app.clientId ? "bg-[#EBF7E6] text-[#2E844A]" : "bg-[#FEF1E5] text-[#DD7A01]"
 )}>
                        {app.clientSecretSet && app.clientId ? 'Configured' : 'Not configured'}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-[#706E6B]">{meta.hint}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-[12px] font-semibold text-[#444444]">Client ID</label>
                        <Input
                          icon={Key}
                          value={app.clientId || ''}
                          onChange={e => setStorageField(provider, 'clientId', e.target.value)}
                          placeholder="Client / App ID"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[12px] font-semibold text-[#444444]">Client Secret</label>
                        <Input
                          icon={Shield}
                          type={showSecrets ? 'text' : 'password'}
                          value={app.clientSecret || ''}
                          onChange={e => setStorageField(provider, 'clientSecret', e.target.value)}
                          placeholder={app.clientSecretSet ? SECRET_MASK_PLACEHOLDER : 'Client / App Secret'}
                        />
                        {app.clientSecretSet && (
                          <p className="text-[11px] text-[#706E6B]">A secret is already saved — leave blank to keep it.</p>
                        )}
                      </div>
                    </div>
                    {provider === 'onedrive' && (
                      <div className="space-y-2 md:w-1/2">
                        <label className="text-[12px] font-semibold text-[#444444]">Azure AD Tenant ID</label>
                        <Input
                          icon={Globe}
                          value={app.tenantId || ''}
                          onChange={e => setStorageField(provider, 'tenantId', e.target.value)}
                          placeholder="common"
                          hint="Use 'common' for multi-tenant, or your Azure AD tenant ID"
                        />
                      </div>
                    )}
                  </div>
                );
              })
            )}

            <div className="flex items-center justify-between">
              <button
                className="text-[#0176D3] text-[13px] font-semibold flex items-center gap-1 hover:underline"
                onClick={() => setShowSecrets(!showSecrets)}
              >
                {showSecrets ? <><EyeOff size={14} /> Mask Secrets</> : <><Eye size={14} /> Reveal Secrets</>}
              </button>
              <button
                className="h-9 px-6 bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold rounded whitespace-nowrap flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                onClick={handleSaveStorageApps}
                disabled={storageSaving || storageLoading}
              >
                {storageSaving ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
                Save
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="w-full pb-10 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <PageHeader title="System Configuration" icon={Settings} subtitle="Manage global parameters, payment interfaces, and core platform limits." />

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
         <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#E0F2FE] border border-[#BAE6FD] text-[12px] font-bold text-[#0176D3]">
               <ShieldCheck size={14} /> Super Admin Priority
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#DCFCE7] border border-[#BBF7D0] text-[12px] font-bold text-[#16A34A]">
               <Activity size={14} className="animate-pulse" /> Network Status: Stable
            </div>
         </div>
         {activeSection !== 'storage' && (
           <button
             className="w-full md:w-auto px-6 h-9 bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold rounded whitespace-nowrap flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
             onClick={handleSave}
             disabled={saving}
           >
             {saving ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
             Preserve Global Config
           </button>
         )}
      </div>

      {loading ? (
        <div className="w-full py-40 flex flex-col items-center justify-center gap-6 opacity-50">
           <Loader size={40} className="animate-spin text-[#0176D3]" />
           <p className="text-[13px] font-semibold text-[#706E6B] animate-pulse">Synchronizing Core Logic…</p>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-8">
          
          {/* Navigation Matrix */}
          <div className="w-full lg:w-80 flex flex-col gap-6">
            <Card className="bg-white p-2 border-[#E5E5E5] shadow-[0_1px_3px_rgba(0,0,0,0.08)] rounded-lg">
               <div className="px-4 py-3 text-[12px] font-bold text-[#444444] uppercase border-b border-[#D8DDE6] mb-2">Settings Sector</div>
               <nav className="space-y-1">
                 {SECTIONS.map(s => (
                    <button
                      key={s.key}
                      className={cn(
 "w-full flex items-center gap-3 px-4 h-9 rounded whitespace-nowrap transition-colors relative",
 activeSection === s.key ? "bg-[#F4F6F9] text-[#0176D3] font-semibold" : "text-[#080707] font-semibold hover:bg-[#F3F2F2]"
 )}
                      onClick={() => setActiveSection(s.key)}
                    >
                      <s.icon size={18} className={cn(activeSection === s.key ? "text-[#0176D3]" : "text-[#706E6B]")} />
                      <span className="text-[13px]">{s.label}</span>
                      {activeSection === s.key && <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#0176D3] rounded-r-md" />}
                    </button>
                  ))}
               </nav>
            </Card>

            <Card className="bg-[#E0F2FE] border-[#BAE6FD] p-6 rounded-lg">
               <div className="flex items-center gap-2 text-[13px] font-bold text-[#0176D3] mb-2">
                  <Info size={16} /> Technical Advisory
               </div>
               <p className="text-[12px] text-[#080707] leading-relaxed">Preserved parameters integrate instantly across all network nodes. High-latency protocols may require a system refresh to reflect architecture shifts.</p>
            </Card>
          </div>

          {/* Configuration Node */}
          <div className="flex-1 min-w-0">
            <Card className="bg-white p-0 overflow-hidden border-[#E5E5E5] shadow-[0_1px_3px_rgba(0,0,0,0.08)] rounded-lg">
              <div className="p-6 border-b border-[#D8DDE6] bg-[#F4F6F9] flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                   <div className={cn(
 "w-12 h-12 rounded-md flex items-center justify-center", 
 activeSection === 'general' ? "bg-[#0176D3] text-white" : "bg-white border border-[#D8DDE6] " + currentSection.color
 )}>
                      <currentSection.icon size={24} />
                   </div>
                   <div className="space-y-1">
                      <h3 className="text-xl font-bold text-[#080707] tracking-tight">{currentSection.label}</h3>
                      <div className="flex items-center gap-2">
                         <div className="w-2 h-2 rounded-full bg-[#2E844A]" />
                         <span className="text-[12px] font-semibold text-[#706E6B]">Configuration</span>
                      </div>
                   </div>
                </div>
                <button
                  onClick={activeSection === 'storage' ? loadStorageApps : load}
                  className="h-9 px-4 bg-white border border-[#C9C9C9] text-[#080707] text-[13px] font-semibold rounded whitespace-nowrap hover:bg-[#F3F2F2] transition-colors flex items-center gap-2"
                >
                  <RefreshCw size={14} /> Refresh Config
                </button>
              </div>
              
              <div className="p-6">
                {renderSection()}
              </div>

              <div className="p-4 bg-[#F4F6F9] border-t border-[#D8DDE6] flex items-center justify-between">
                 <div className="flex items-center gap-2 text-[11px] font-semibold text-[#706E6B] uppercase ">
                    <Shield size={14} className="text-[#0176D3]" /> Security: End-to-End Encryption Enabled
                 </div>
                 <div className="text-[11px] font-mono text-[#706E6B]">Ref: SYS_CONFIG_{activeSection.toUpperCase()}</div>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
